import { useCallback, useEffect, useRef, useState } from 'react';
import { canRelayPrivateVoices, sessionApiUrl, sessionOrigin, sessionSocketUrl } from './sessionEndpoint.js';
import { clockSyncSample, promptWithServerClockOffset } from './sessionMessages.js';
import { libraryRequest } from '../core/preparedLibrary.js';
import { readBoundedArrayBuffer } from '../../cloudflare/requestBody.js';

// This key and Actor-tab isolation contract come from permitted regression tests.
const IDENTITY_KEY = 'rehearsal-director:identity';
function identityStorage() {
  const tab = new URLSearchParams(globalThis.location?.search || '').get('actorTab') === '1';
  return tab ? sessionStorage : localStorage;
}
export function savedIdentity() {
  try { const raw = identityStorage().getItem(IDENTITY_KEY); return raw ? JSON.parse(raw) : null; }
  catch { return null; }
}
export function enqueuePrivatePrompt(queue, prompt, options = {}) {
  if (!prompt?.id) return queue;
  if (queue.some(value => value.deliveryId && value.deliveryId === prompt.deliveryId)) return queue;
  if (options.recovered && queue.some(value => value.id === prompt.id)) return queue;
  if (options.replace) {
    const index = queue.findIndex(value => value.id === prompt.id);
    if (index >= 0) return queue.map((value, i) => i === index ? prompt : value);
  }
  const result = [...queue, prompt], limit = options.limit || 20;
  return result.length <= limit ? result : [result[0], ...result.slice(-(limit - 1))];
}
export const removePrivatePrompt = (queue, id) => queue.filter(prompt => prompt.id !== id && prompt.deliveryId !== id);
export function updateCurrentPrivatePrompt(queue, updater) {
  const current = queue[0] || null, next = updater(current);
  return next === current ? queue : next ? [next, ...queue.slice(1)] : queue.slice(1);
}
export function reconcileRecoveryQueue(queue, room, aliases = new Map()) {
  if (!room || ['complete', 'lobby'].includes(room.status) || room.ended) return [];
  if (!Array.isArray(room.pendingPromptRefs)) return queue;
  const result = [];
  for (const ref of room.pendingPromptRefs) {
    const value = queue.find(prompt => prompt.deliveryId === ref.deliveryId)
      || queue.find(prompt => prompt.id === ref.id && aliases.get(ref.deliveryId) === prompt.deliveryId)
      || (!ref.deliveryId ? queue.find(prompt => prompt.id === ref.id) : null);
    if (!value) break; // A missing authoritative head must arrive before later notes can play.
    result.push(value);
  }
  return result;
}
export async function fulfillVoiceJob(job, identity, {allowGeneration=false}={}) {
  if (!allowGeneration || !canRelayPrivateVoices() || identity?.role !== 'host' || !job?.jobId) return false;
  const headers = { authorization: 'Bearer ' + identity.token };
  const target = sessionApiUrl('/api/rooms/' + encodeURIComponent(identity.roomCode) + '/voice-jobs/' + encodeURIComponent(job.jobId));
  let audio, failure;
  try {
    const response = await fetch('/api/voices/preview', { method: 'POST', headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ profileId: job.profileId, text: job.text, ...(job.speed === undefined ? {} : { speed: job.speed }) }),
      signal: AbortSignal.timeout(90000), cache: 'no-store', redirect: 'error' });
    if (!response.ok) { const detail = await response.json(); failure = detail.code === 'VOICE_INPUT_LIMIT' ? 'capacity-exceeded' : 'generation-failed'; }
    else { audio = await response.arrayBuffer(); if (!audio.byteLength || audio.byteLength > 32 * 1024 * 1024) failure = 'generation-failed'; }
  } catch { failure = 'generation-failed'; }
  const result = await fetch(target, { method: 'POST', headers: { ...headers, ...(failure ? { 'x-qline-voice-error': failure } : { 'content-type': 'audio/wav' }) },
    ...(failure ? {} : { body: audio }), signal: AbortSignal.timeout(90000), cache: 'no-store', redirect: 'error' });
  return result.ok && !failure;
}
export async function requestJson(url, options = {}) {
  const controller = new AbortController(); let timer;
  const deadline = new Promise((_, reject) => { timer = setTimeout(() => {
    controller.abort(); reject(Error('The room took too long to respond. Please try again.'));
  }, 15000); });
  try {
    return await Promise.race([deadline, (async () => {
      const response = await fetch(url, { ...options, signal: options.signal ? AbortSignal.any([options.signal, controller.signal]) : controller.signal });
      const value = response.body || typeof response.arrayBuffer==='function' ? JSON.parse(new TextDecoder().decode(await readBoundedArrayBuffer(response,4*1024*1024,'Room response is too large.',controller.signal))) : await response.json();
      if(JSON.stringify(value).length>4*1024*1024)throw Error('Room response is too large.');
      if (!response.ok) throw Object.assign(Error(value.message || 'The room service is unavailable.'), { status: response.status });
      return value;
    })()]);
  } finally { clearTimeout(timer); controller.abort(); }
}
function makeSessionOwner(setters) {
  return { ...setters, alive: true, mounted: false, transport: 0, entry: 0, socket: null, retry: null, entryAbort: null,
    identity: null, storagePending: undefined, creation: null, entryBusy: false, aliases: new Map(), pool: [], latestRoom: null, voiceJobs: new Set(), clockOffset: 0 };
}
function persistIdentity(owner, value) {
  owner.storagePending = value;
  try {
    const storage = identityStorage();
    if (value) { const encoded = JSON.stringify(value); storage.setItem(IDENTITY_KEY, encoded); if (storage.getItem(IDENTITY_KEY) !== encoded) throw Error('Not saved'); }
    else { storage.removeItem(IDENTITY_KEY); if (storage.getItem(IDENTITY_KEY) !== null) throw Error('Not cleared'); }
    owner.storagePending = undefined; owner.setStorageNotice(''); return true;
  } catch {
    owner.setStorageNotice(value ? 'Room access could not be saved. Keep this tab open; reload may lose access. Retry saved room access.'
      : 'Room access could not be cleared. Do not reload until you retry saved room access.'); return false;
  }
}
function stopTransport(owner) {
  owner.transport++; if (owner.retry) window.clearTimeout(owner.retry); owner.retry = null;
  const old = owner.socket; owner.socket = null; try { old?.close(); } catch {}
}
function clearSession(owner, notice) {
  stopTransport(owner); owner.identity = null; owner.aliases.clear(); owner.pool = []; owner.latestRoom = null;
  owner.setIdentity(null); owner.setRoom(null); owner.setQueue([]); owner.setHistory([]);
  owner.setConnection('ready'); owner.setNotice(notice); persistIdentity(owner, null);
}
function applyRoom(owner, room) {
  owner.latestRoom = room; owner.setRoom(room);
  owner.setQueue(queue => { owner.pool = queue.filter(prompt => !Array.isArray(room.pendingPromptRefs) || room.pendingPromptRefs.some(ref => ref.id === prompt.id)); return reconcileRecoveryQueue(owner.pool, room, owner.aliases); });
}
function receiveSession(owner, message, mine) {
  if (!owner.alive || mine !== owner.transport || !message || typeof message.type !== 'string') return;
  if (message.type === 'identity' && message.identity?.roomCode === owner.identity?.roomCode && message.identity.role === owner.identity.role) {
    owner.identity = { ...message.identity, name: owner.identity.name || message.identity.name };
    owner.setIdentity(owner.identity); persistIdentity(owner, owner.identity);
  } else if (message.type === 'room_state' && message.room?.code === owner.identity?.roomCode) applyRoom(owner, message.room);
  else if (message.type === 'room_closed') clearSession(owner, typeof message.message === 'string' ? message.message : 'The rehearsal room ended.');
  else if (message.type === 'error') owner.setNotice(typeof message.message === 'string' ? message.message : 'The room needs attention.');
  else if (message.type === 'clock_pong') {
    const sample = clockSyncSample(message.clientSentAt, message.serverNow);
    if (sample) owner.clockOffset = sample.serverOffsetMs;
  } else if (message.type === 'private_prompt' || message.type === 'public_cue') {
    const source = message.prompt || message.cue;
    if (!source?.id) return;
    const prompt = promptWithServerClockOffset({ ...source, ...(message.deliveryId ? { deliveryId: message.deliveryId } : {}) }, owner.clockOffset);
    owner.setHistory(history => enqueuePrivatePrompt(history, prompt, { limit: 200 }));
    if (message.type === 'private_prompt') owner.setQueue(queue => {
      const owned = [...queue, ...owner.pool].find(value => value.id === prompt.id);
      if (message.recovered && owned?.deliveryId && prompt.deliveryId !== owned.deliveryId) owner.aliases.set(prompt.deliveryId, owned.deliveryId);
      const waiting = message.recovered ? owner.pool : queue;
      owner.pool = enqueuePrivatePrompt(waiting, prompt, { recovered: message.recovered, replace: message.replaceExisting });
      return message.recovered && owner.latestRoom ? reconcileRecoveryQueue(owner.pool, owner.latestRoom, owner.aliases) : owner.pool;
    });
  } else if (message.type === 'prompt_complete') owner.setQueue(queue => removePrivatePrompt(queue, message.deliveryId || message.cueId || message.promptId));
  else if (message.type === 'voice_job' && canRelayPrivateVoices()) {
    if(!owner.allowGeneration){owner.setNotice('Audio generation is disabled. Use verified recordings or supported text fallback.');return;}
    const job = message.job || message;
    if (job.jobId && !owner.voiceJobs.has(job.jobId)) { owner.voiceJobs.add(job.jobId); void fulfillVoiceJob(job, owner.identity, {allowGeneration:owner.allowGeneration}).catch(() => {}); }
  }
}
function scheduleRecovery(owner, mine) {
  if (!owner.alive || mine !== owner.transport || !owner.identity || owner.retry) return;
  owner.setConnection('offline');
  owner.retry = window.setTimeout(() => { owner.retry = null; void recoverSession(owner, mine); }, 1200);
}
function connectSession(owner, mine) {
  if (!owner.alive || mine !== owner.transport || !owner.identity) return;
  const socket = new WebSocket(sessionSocketUrl(owner.identity)); owner.socket = socket;
  socket.addEventListener('open', () => {
    if (!owner.alive || mine !== owner.transport || socket !== owner.socket) return;
    owner.setConnection('connected'); socket.send(JSON.stringify({ type: 'clock_ping', clientSentAt: Date.now() }));
  });
  socket.addEventListener('message', event => {
    if (socket !== owner.socket) return;
    try { receiveSession(owner, JSON.parse(event.data), mine); } catch { if (mine === owner.transport) owner.setNotice('A room update was invalid. Reconnect to recover it.'); }
  });
  socket.addEventListener('close', () => { if (socket === owner.socket) { owner.socket = null; scheduleRecovery(owner, mine); } });
  socket.addEventListener('error', () => { if (socket === owner.socket) owner.setConnection('offline'); });
}
async function recoverSession(owner, mine) {
  if (!owner.identity || !owner.alive || mine !== owner.transport) return;
  const identity = owner.identity;
  try {
    const value = await requestJson(sessionApiUrl('/api/rooms/' + encodeURIComponent(identity.roomCode) + '/session'),
      { headers: { authorization: 'Bearer ' + identity.token }, cache: 'no-store', redirect: 'error' });
    if (!owner.alive || mine !== owner.transport) return;
    if (!value.room || value.room.code !== identity.roomCode) throw Error('Room response was invalid');
    applyRoom(owner, value.room); connectSession(owner, mine);
  } catch (error) {
    if (owner.alive && mine === owner.transport && [401, 403, 404, 410].includes(error.status)) clearSession(owner, 'Your previous room has ended. Create a new room or join the new code.');
    else scheduleRecovery(owner, mine);
  }
}
function cancelSessionEntry(owner) {
  owner.entry++; owner.entryAbort?.abort(); owner.entryAbort = null; owner.entryBusy = false; owner.setEntering(false);
}
function adoptIdentity(owner, identity, room) {
  stopTransport(owner); owner.aliases.clear(); owner.pool = []; owner.latestRoom = room; owner.identity = identity;
  owner.setIdentity(identity); owner.setRoom(room); owner.setQueue([]); owner.setHistory([]);
  persistIdentity(owner, identity); owner.setConnection('connecting');
  if (owner.mounted && typeof WebSocket !== 'undefined') connectSession(owner, owner.transport);
}
async function useSavedScene(owner, selection, entry) {
  const identity = owner.identity, before = owner.transport;
  if (!identity || identity.role !== 'host') return false;
  try {
    const value = await libraryRequest('use', { scope: selection.scope, id: selection.id }, { key: selection.key, room: identity, signal: owner.entryAbort?.signal });
    if (!owner.alive || entry !== owner.entry || before !== owner.transport) return false;
    owner.setRoom(current => current?.scenario?.id === value.room?.scenario?.id ? current : value.room || current);
    owner.setPreparationRevision(value => value + 1); owner.setNotice(value.message || 'Saved scene loaded.'); return true;
  } catch (error) { if (owner.alive && entry === owner.entry) owner.setNotice(error.message); return false; }
}
async function enterSession(owner, type, input, selection) {
  if (!owner.alive || type === 'create' && owner.entryBusy) return false;
  if (owner.entryBusy) cancelSessionEntry(owner);
  owner.entryBusy = true; const mine = ++owner.entry; owner.entryAbort = new AbortController(); owner.setEntering(true);
  try {
    const body = type === 'create' ? { name: input.name, scenario: input.scenario, audioConfig: input.audioConfig } : { name: input.name };
    if (type === 'create') {
      const fingerprint = JSON.stringify(body);
      if (owner.creation?.fingerprint !== fingerprint) owner.creation = { fingerprint, id: crypto.randomUUID() };
      body.creationId = owner.creation.id;
    }
    const value = await requestJson(sessionApiUrl(type === 'create' ? '/api/rooms' : '/api/rooms/' + input.code.toUpperCase() + '/join'),
      { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body), signal: owner.entryAbort.signal, cache: 'no-store', redirect: 'error' });
    if (!owner.alive || mine !== owner.entry) return false;
    if (!value.identity?.token || !value.identity.roomCode || !value.room) throw Error('Room access was invalid. Retry.');
    const identity = { ...value.identity, ...(value.identity.name ? {} : { name: input.name }) };
    adoptIdentity(owner, identity, value.room); owner.creation = null;
    owner.setNotice(type === 'create' ? 'Rehearsal room created.' : 'You joined the cast.');
    return selection ? await useSavedScene(owner, selection, mine) : true;
  } catch (error) { if (owner.alive && mine === owner.entry) owner.setNotice(error.message); return false; }
  finally { if (mine === owner.entry) { owner.entryBusy = false; owner.setEntering(false); } }
}
export function useSession({allowGeneration=false}={}) {
  const [connection, setConnection] = useState('ready');
  const [identity, setIdentity] = useState(savedIdentity);
  const [room, setRoom] = useState(null);
  const [privateQueue, setQueue] = useState([]);
  const [promptHistory, setHistory] = useState([]);
  const [notice, setNotice] = useState('');
  const [storageNotice, setStorageNotice] = useState('');
  const [entering, setEntering] = useState(false);
  const [preparationRevision, setPreparationRevision] = useState(0);
  const holder = useRef(null);
  if (!holder.current) { holder.current = makeSessionOwner({ setConnection, setIdentity, setRoom, setQueue, setHistory, setNotice, setStorageNotice, setEntering, setPreparationRevision }); holder.current.identity = identity; }
  const owner = holder.current;owner.allowGeneration=allowGeneration===true;
  useEffect(() => {
    owner.alive = true; owner.mounted = true; owner.identity = identity; const mine = owner.transport;
    if (owner.identity) { setConnection('connecting'); void recoverSession(owner, mine); }
    return () => { owner.alive = false; cancelSessionEntry(owner); stopTransport(owner); owner.identity = null; };
  }, []);
  const send = useCallback(message => {
    if (owner.socket?.readyState !== 1) { setNotice('Reconnecting to the rehearsal…'); return false; }
    owner.socket.send(JSON.stringify(message)); return true;
  }, []);
  return { connection, identity, room, privatePrompt: privateQueue[0] || null, promptHistory, notice, storageNotice, entering, preparationRevision,
    publicBaseUrl: sessionOrigin(), canHost: canRelayPrivateVoices(), canProvideSpeech: allowGeneration===true && canRelayPrivateVoices(), entryPending: entering, setNotice,
    setPrivatePrompt(value) { setQueue(queue => value === null ? [] : typeof value === 'function' ? updateCurrentPrivatePrompt(queue, value) : enqueuePrivatePrompt(queue, value)); },
    createRoom(name, scenario, audioConfig, selection) { return enterSession(owner, 'create', { name, scenario, audioConfig }, selection); },
    joinRoom(code, name) { return enterSession(owner, 'join', { code, name }); },
    cancelEntry() { cancelSessionEntry(owner); },
    useLibraryScene(selection) { cancelSessionEntry(owner); owner.entryAbort = new AbortController(); return useSavedScene(owner, selection, owner.entry); },
    retryIdentityStorage() { return owner.storagePending === undefined || persistIdentity(owner, owner.storagePending); },
    leaveRoom() {
      if (owner.identity?.role === 'host' && owner.socket?.readyState !== 1) { setNotice('The room is reconnecting. Director access is retained; reconnect before ending it.'); return false; }
      if (owner.socket?.readyState === 1) owner.socket.send(JSON.stringify({ type: 'leave_room' }));
      cancelSessionEntry(owner); clearSession(owner, 'You left the rehearsal room.'); return true;
    }, send,
  };
}
